import React from 'react';
import { Image, Video } from 'lucide-react';

interface MediaTypeSelectorProps {
  onMediaTypeSelected: (type: MediaType) => void;
}

const MediaTypeSelector: React.FC<MediaTypeSelectorProps> = ({ onMediaTypeSelected }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">Choose Output Media Type</h2>
        <p className="text-gray-400">
          Select what type of content you want to generate with AI
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-8">
        {/* Images Option */}
        <button
          onClick={() => onMediaTypeSelected('image')}
          className="group relative bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-2 border-purple-500/30 rounded-2xl p-8 hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 hover:scale-105 active:scale-100"
        >
          <div className="flex flex-col items-center gap-6">
            <div className="p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl group-hover:scale-110 transition-transform">
              <Image className="w-16 h-16 text-purple-400" />
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-2">Generate Images</h3>
              <p className="text-gray-400 text-sm">
                Create product photos, lifestyle images, and visual assets with AI
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-purple-300">
              <span>✨</span>
              <span>AI-powered image generation</span>
            </div>
          </div>

          {/* Hover Effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300" />
        </button>

        {/* Videos Option */}
        <button
          onClick={() => onMediaTypeSelected('video')}
          className="group relative bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/30 rounded-2xl p-8 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 hover:scale-105 active:scale-100"
        >
          <div className="flex flex-col items-center gap-6">
            <div className="p-6 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl group-hover:scale-110 transition-transform">
              <Video className="w-16 h-16 text-blue-400" />
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-2">Generate Videos</h3>
              <p className="text-gray-400 text-sm">
                Create product demos, animated content, and video assets with AI
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-blue-300">
              <span>🎬</span>
              <span>AI-powered video generation</span>
            </div>
          </div>

          {/* Hover Effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/5 group-hover:to-cyan-500/5 transition-all duration-300" />
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 max-w-4xl mx-auto mt-8">
        <p className="text-sm text-blue-300">
          <strong>💡 Tip:</strong> Both options will process your input images from BigQuery. 
          This choice determines what the AI will <strong>generate as output</strong> - new images or videos based on your prompts.
        </p>
      </div>
    </div>
  );
};

export default MediaTypeSelector;
